import { manualSignalEntryBase } from "@healthos/client-data";
import type { api } from "@healthos/schemas";
import {
  CONFOUNDER_DURATION_LABELS,
  CONFOUNDER_REASON_LABELS,
  CONNECTION_TYPE_LABELS,
  CONNECTION_WHO_LABELS,
  type ConfounderDuration,
  type ConfounderReason,
  type ConnectionType,
  type ConnectionWho,
  classifyWorkout,
  connectionTypeSchema,
  connectionWhoSchema,
  type HealthBehaviour,
  workoutMinutes,
} from "@healthos/shared";

export const MANUAL_LOG_ACTION_IDS = [
  "food",
  "hydration",
  "sleep",
  "mood",
  "symptoms",
  "caffeine",
  "workout",
  "medication",
  "vitals",
  "disruptor",
  "connection",
] as const;

export type ManualLogActionId = (typeof MANUAL_LOG_ACTION_IDS)[number];

export type ManualLogForm = {
  occurredAt: string;
  note: string;
  foodMeal: string;
  foodMealType: string;
  hydrationAmountMl: string;
  hydrationBeverage: string;
  sleepKind: "quality_note" | "nap";
  sleepQuality: "poor" | "ok" | "good" | "great" | "restless";
  sleepHours: string;
  napMinutes: string;
  moodLabel: string;
  mood: string;
  energy: string;
  stress: string;
  focus: string;
  symptomLabel: string;
  symptomSeverity: string;
  intakeType: api.ManualIntakeType;
  intakeLabel: string;
  caffeineCups: string;
  caffeineMg: string;
  alcoholServings: string;
  workoutActivity: string;
  workoutDurationMinutes: string;
  workoutIntensity: "easy" | "moderate" | "hard";
  medication: string;
  medicationStatus: api.ManualMedicationStatus;
  doseAmount: string;
  injectionSite: string;
  cadenceDays: string;
  vitalsMetric: api.ManualVitalsMetric;
  kg: string;
  systolic: string;
  diastolic: string;
  glucoseMgDl: string;
  bpm: string;
  hrvMs: string;
  temperatureC: string;
  hba1cPercent: string;
  // Confounder ("disruptor") fields. reason and duration are member-chosen;
  // the rest are context carried from the card (never edited in the form) so
  // the server can stamp families and join the acknowledgement to the reading.
  confounderReason: ConfounderReason;
  confounderDuration: ConfounderDuration;
  confounderCardFamily: "" | HealthBehaviour;
  confounderDecisionId: string;
  confounderMicrostepId: string;
  confounderSourceSurface: "today_card" | "add_to_today";
  // Connection fields. who is member-chosen but optional, matching the API's
  // optional `who`; an empty string means "not chosen" and is omitted from
  // the request rather than sent as an invalid enum value.
  connectionType: "" | ConnectionType;
  connectionWho: "" | ConnectionWho;
  connectionRating: string;
};

export type ManualLogAction = {
  id: ManualLogActionId;
  title: string;
  description: string;
};

export const MANUAL_LOG_ACTIONS: readonly ManualLogAction[] = [
  {
    id: "food",
    title: "Log food",
    description: "Meal timing, nourishment, appetite, or a quick note.",
  },
  {
    id: "hydration",
    title: "Log hydration",
    description: "Water, electrolytes, or another fluid note.",
  },
  {
    id: "sleep",
    title: "Log sleep",
    description: "Sleep quality, short nights, naps, or recovery context.",
  },
  {
    id: "mood",
    title: "Check in",
    description: "Mood, energy, stress, or focus.",
  },
  {
    id: "symptoms",
    title: "Log symptoms",
    description: "Side effects, pain, nausea, appetite, or fatigue.",
  },
  {
    id: "caffeine",
    title: "Log caffeine or alcohol",
    description: "Caffeine, alcohol, timing, or sleep-impacting intake.",
  },
  {
    id: "workout",
    title: "Add workout",
    description: "Walk, run, swim, strength, or another activity.",
  },
  {
    id: "medication",
    title: "Log medication",
    description: "Record taken, skipped, moved, or a dose-window note.",
  },
  {
    id: "vitals",
    title: "Log vitals",
    description: "Weight, blood pressure, glucose, or another manual reading.",
  },
  {
    id: "disruptor",
    title: "Something affecting you?",
    description:
      "Flag heat, illness, travel, or something else outside your control so we ease off.",
  },
  {
    id: "connection",
    title: "Connection",
    description: "Time with people: who, how, and how it felt.",
  },
];

export function createManualLogForm(): ManualLogForm {
  return {
    occurredAt: localDateTimeValue(),
    note: "",
    foodMeal: "",
    foodMealType: "Lunch",
    hydrationAmountMl: "500",
    hydrationBeverage: "Water",
    sleepKind: "quality_note",
    sleepQuality: "good",
    sleepHours: "7.5",
    napMinutes: "20",
    moodLabel: "Calm",
    mood: "3",
    energy: "3",
    stress: "2",
    focus: "3",
    symptomLabel: "Nausea",
    symptomSeverity: "3",
    intakeType: "coffee",
    intakeLabel: "Coffee",
    caffeineCups: "1",
    caffeineMg: "95",
    alcoholServings: "1",
    workoutActivity: "Walk",
    workoutDurationMinutes: "30",
    workoutIntensity: "moderate",
    medication: "",
    medicationStatus: "taken",
    doseAmount: "",
    injectionSite: "Abdomen",
    cadenceDays: "7",
    vitalsMetric: "weight",
    kg: "75",
    systolic: "120",
    diastolic: "80",
    glucoseMgDl: "100",
    bpm: "62",
    hrvMs: "45",
    temperatureC: "36.8",
    hba1cPercent: "6",
    confounderReason: "heat",
    confounderDuration: "few_nights",
    confounderCardFamily: "",
    confounderDecisionId: "",
    confounderMicrostepId: "",
    confounderSourceSurface: "add_to_today",
    connectionType: "",
    connectionWho: "",
    connectionRating: "3",
  };
}

export type ManualMedicationDefaults = Pick<
  ManualLogForm,
  "medication" | "doseAmount" | "cadenceDays"
>;

/**
 * Prefer the most recent logged dose, then the medication declared during
 * onboarding. The caller applies this only to an untouched medication form,
 * so a member's in-progress choice always wins.
 */
export function resolveManualMedicationDefaults(input: {
  currentDose?: api.CurrentDose | null;
  onboardingMedication?: string | null;
  onboardingDoseMg?: number | null;
  onboardingCadenceDays?: number | null;
}): ManualMedicationDefaults | null {
  const recentMedication = nonEmptyText(input.currentDose?.medication);
  if (recentMedication) {
    return {
      medication: recentMedication,
      doseAmount: positiveNumberText(input.currentDose?.doseAmount),
      cadenceDays: positiveNumberText(input.currentDose?.cadenceDays),
    };
  }

  const onboardingMedication = nonEmptyText(input.onboardingMedication);
  if (!onboardingMedication) return null;

  return {
    medication: onboardingMedication,
    doseAmount: positiveNumberText(input.onboardingDoseMg),
    cadenceDays: positiveNumberText(input.onboardingCadenceDays),
  };
}

export function buildManualSignalRequest(
  actionId: ManualLogActionId,
  form: ManualLogForm,
): { payload: api.RecordManualSignalEntryRequest } | { error: string } {
  const base = baseEntry(form);
  if (!base) return { error: "Choose when this happened before saving." };

  switch (actionId) {
    case "food": {
      const meal = requiredText(form.foodMeal, "Add what you ate before saving.");
      if (!meal.ok) return meal;
      return {
        payload: {
          ...base,
          type: "food",
          meal: meal.value,
          mealType: form.foodMealType,
          quickAction: form.foodMealType,
        },
      };
    }
    case "hydration": {
      const amountMl = positiveNumber(
        form.hydrationAmountMl,
        "Add an amount of fluid before saving.",
      );
      if (!amountMl.ok) return amountMl;
      return {
        payload: {
          ...base,
          type: "hydration",
          amountMl: Math.round(amountMl.value),
          beverage: form.hydrationBeverage,
          quickAction: form.hydrationBeverage,
        },
      };
    }
    case "sleep": {
      const sleepHours =
        form.sleepKind === "nap"
          ? positiveNumber(form.napMinutes, "Add a nap length before saving.", 60)
          : positiveNumber(form.sleepHours, "Add a sleep length before saving.");
      if (!sleepHours.ok) return sleepHours;
      /*
       * A quality note carries no duration server-side, by design: it records
       * how the night felt, not how long it ran. The form collects a sleep
       * length regardless, and the quick-picks ("Slept well", "Slept okay",
       * "Poor sleep", "Restless", "Short night") all set quality_note, so
       * a member entered 7.5 hours and it was dropped before it reached the
       * sleep pillar. A note with a length is a reported night with a quality
       * attached; send it as one, and the quality rides along either way.
       */
      const reportsDuration = form.sleepKind === "quality_note" && sleepHours.value > 0;
      const sleepKind = reportsDuration ? "night_sleep" : form.sleepKind;
      return {
        payload: {
          ...base,
          type: "sleep",
          sleepKind,
          sleepHours: sleepHours.value,
          ...(sleepKind !== "nap" ? { quality: form.sleepQuality } : {}),
          label: form.sleepKind === "nap" ? "Nap" : `${sleepQualityLabel(form.sleepQuality)} sleep`,
          quickAction: form.sleepKind === "nap" ? "Nap" : sleepQualityLabel(form.sleepQuality),
        },
      };
    }
    case "mood":
      return {
        payload: {
          ...base,
          type: "mood",
          label: form.moodLabel,
          mood: boundedNumber(form.mood, 1, 5),
          energy: boundedNumber(form.energy, 1, 5),
          stress: boundedNumber(form.stress, 1, 5),
          focus: boundedNumber(form.focus, 1, 5),
          quickAction: form.moodLabel,
        },
      };
    case "symptoms": {
      const symptom = requiredText(form.symptomLabel, "Add a symptom before saving.");
      if (!symptom.ok) return symptom;
      return {
        payload: {
          ...base,
          type: "symptoms",
          symptoms: [symptom.value],
          severity: boundedNumber(form.symptomSeverity, 1, 10),
          label: symptom.value,
          quickAction: symptom.value,
        },
      };
    }
    case "caffeine": {
      const intakeType = form.intakeType;
      const isAlcohol = intakeType === "alcohol";
      const isNone = intakeType === "none";
      const amountLabel = isNone
        ? "No caffeine or alcohol"
        : isAlcohol
          ? `${form.alcoholServings || "0"} servings`
          : `${form.caffeineCups || "0"} cups`;
      return {
        payload: {
          ...base,
          type: "caffeine",
          intakeType,
          amountLabel,
          ...(!isAlcohol && !isNone ? { caffeineMg: nonnegativeNumber(form.caffeineMg) } : {}),
          ...(isAlcohol ? { alcoholServings: nonnegativeNumber(form.alcoholServings) } : {}),
          quickAction: form.intakeLabel,
        },
      };
    }
    case "workout": {
      const durationMinutes = positiveNumber(
        form.workoutDurationMinutes,
        "Add a workout duration before saving.",
      );
      if (!durationMinutes.ok) return durationMinutes;
      return {
        payload: {
          ...base,
          type: "workout",
          activity: form.workoutActivity,
          durationMinutes: Math.round(durationMinutes.value),
          intensity: form.workoutIntensity,
          quickAction: form.workoutActivity,
        },
      };
    }
    case "medication": {
      const medication = requiredText(form.medication, "Add a medication name before saving.");
      if (!medication.ok) return medication;
      const doseAmount =
        form.medicationStatus === "taken"
          ? positiveNumber(form.doseAmount, "Add a dose amount before saving.")
          : null;
      if (doseAmount && !doseAmount.ok) return doseAmount;
      return {
        payload: {
          ...base,
          type: "medication",
          medication: medication.value,
          status: form.medicationStatus,
          doseUnit: "mg",
          ...(doseAmount?.ok ? { doseAmount: doseAmount.value } : {}),
          ...(form.injectionSite.trim() ? { injectionSite: form.injectionSite.trim() } : {}),
          ...(positiveNumber(form.cadenceDays, "").ok
            ? { cadenceDays: Number(form.cadenceDays) }
            : {}),
          quickAction: medication.value,
        },
      };
    }
    case "vitals":
      return vitalsRequest(base, form);
    case "disruptor":
      return {
        payload: {
          ...base,
          type: "disruptor",
          reason: form.confounderReason,
          duration: form.confounderDuration,
          ...(form.confounderCardFamily ? { cardFamily: form.confounderCardFamily } : {}),
          sourceSurface: form.confounderSourceSurface,
          ...(form.confounderDecisionId ? { decisionId: form.confounderDecisionId } : {}),
          ...(form.confounderMicrostepId ? { microstepId: form.confounderMicrostepId } : {}),
        },
      };
    case "connection": {
      const connectionType = form.connectionType;
      if (!connectionType) return { error: "Choose how you connected before saving." };
      return {
        payload: {
          ...base,
          type: "connection",
          connectionType,
          rating: boundedNumber(form.connectionRating, 1, 5),
          ...(form.connectionWho ? { who: form.connectionWho } : {}),
          quickAction: CONNECTION_TYPE_LABELS[connectionType],
        },
      };
    }
  }
}

function vitalsRequest(
  base: BaseEntry,
  form: ManualLogForm,
): { payload: api.RecordManualSignalEntryRequest } | { error: string } {
  const metric = form.vitalsMetric;
  const common = {
    ...base,
    type: "vitals" as const,
    metric,
    quickAction: vitalMetricLabel(metric),
  };

  if (metric === "blood_pressure") {
    const systolic = positiveNumber(form.systolic, "Add the systolic reading before saving.");
    const diastolic = positiveNumber(form.diastolic, "Add the diastolic reading before saving.");
    if (!systolic.ok) return systolic;
    if (!diastolic.ok) return diastolic;
    return { payload: { ...common, systolic: systolic.value, diastolic: diastolic.value } };
  }

  const fieldByMetric = {
    weight: ["kg", form.kg, "Add a weight before saving."],
    glucose: ["glucoseMgDl", form.glucoseMgDl, "Add a glucose reading before saving."],
    resting_hr: ["bpm", form.bpm, "Add a heart rate before saving."],
    hrv: ["hrvMs", form.hrvMs, "Add an HRV reading before saving."],
    temperature: ["temperatureC", form.temperatureC, "Add a temperature before saving."],
    hba1c: ["hba1cPercent", form.hba1cPercent, "Add an HbA1c reading before saving."],
  } as const;
  const [field, value, error] = fieldByMetric[metric];
  const parsed = positiveNumber(value, error);
  if (!parsed.ok) return parsed;
  return { payload: { ...common, [field]: parsed.value } as api.ManualVitalsEntryRequest };
}

type BaseEntry = {
  occurredAt: string;
  note?: string;
};

function baseEntry(form: ManualLogForm): BaseEntry | null {
  return manualSignalEntryBase({ occurredAt: form.occurredAt, note: form.note });
}

function localDateTimeValue(date = new Date()): string {
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.valueOf() - offsetMs).toISOString().slice(0, 16);
}

/**
 * When a sleep entry defaults to.
 *
 * "Now" is right for a meal or a glass of water and wrong for a night: someone
 * logging at teatime is describing last night, not this afternoon. The read
 * attributes a night to the day it began and excludes the in-progress day, so
 * a night stamped this afternoon is filed under today and cannot count until
 * tomorrow. Defaulting to last night files it where it belongs, and it counts
 * straight away.
 */
export function defaultSleepOccurredAt(now = new Date()): string {
  const lastNight = new Date(now);
  lastNight.setDate(lastNight.getDate() - 1);
  lastNight.setHours(23, 0, 0, 0);
  return localDateTimeValue(lastNight);
}

function requiredText(
  value: string,
  error: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const trimmed = value.trim();
  return trimmed ? { ok: true, value: trimmed } : { ok: false, error };
}

function nonEmptyText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function positiveNumberText(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? String(value) : "";
}

function positiveNumber(
  value: string,
  error: string,
  divisor = 1,
): { ok: true; value: number } | { ok: false; error: string } {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0
    ? { ok: true, value: parsed / divisor }
    : { ok: false, error };
}

function nonnegativeNumber(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function boundedNumber(value: string, min: number, max: number): number {
  const parsed = Math.round(Number(value));
  return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : min;
}

function capitalize(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}

/**
 * Rehydrate the log form from a stored manual-entry payload, for editing an
 * existing manual signal. Inverse of buildManualSignalRequest at the level of
 * the persisted signal payload (whose keys differ from the request body). Reads
 * the same payload keys the mobile edit sheet does so both clients agree on what
 * an entry decodes to. Returns null for an entry type the log form cannot edit.
 *
 * `type` is the manualEntry.type ("food", "vitals", ...); `occurredAt` is the
 * entry's ISO instant; `payload` is manualEntry.payload from the signal timeline.
 */
export function manualLogFormFromEntry(
  type: string,
  payload: Record<string, unknown>,
  occurredAt: string,
): { actionId: ManualLogActionId; form: ManualLogForm } | null {
  const actionId = manualLogActionIdForEntryType(type);
  if (!actionId) return null;

  const form: ManualLogForm = {
    ...createManualLogForm(),
    occurredAt: isoToLocalDateTimeValue(occurredAt) ?? createManualLogForm().occurredAt,
    note: cleanManualNote(payloadString(payload.note) ?? ""),
  };

  switch (actionId) {
    case "food":
      return {
        actionId,
        form: {
          ...form,
          foodMealType: payloadString(payload.mealType) ?? form.foodMealType,
          foodMeal:
            payloadString(payload.name) ??
            payloadString(payload.meal) ??
            payloadString(payload.label) ??
            "",
        },
      };
    case "hydration":
      return {
        actionId,
        form: {
          ...form,
          hydrationAmountMl: numberToField(payload.ml ?? payload.value, form.hydrationAmountMl),
          hydrationBeverage: payloadString(payload.beverage) ?? form.hydrationBeverage,
        },
      };
    case "sleep": {
      const isNap = payloadString(payload.sleepKind) === "nap" || payload.isNap === true;
      const hours = payloadNumber(payload.hours);
      return {
        actionId,
        form: {
          ...form,
          sleepKind: isNap ? "nap" : "quality_note",
          sleepQuality: sleepQualityFromPayload(payloadString(payload.quality), form.sleepQuality),
          sleepHours: isNap ? form.sleepHours : hours === null ? form.sleepHours : String(hours),
          napMinutes: isNap && hours !== null ? String(Math.round(hours * 60)) : form.napMinutes,
        },
      };
    }
    case "mood":
      return {
        actionId,
        form: {
          ...form,
          moodLabel: payloadString(payload.label) ?? form.moodLabel,
          mood: numberToField(payload.mood, form.mood),
          energy: numberToField(payload.energy, form.energy),
          stress: numberToField(payload.stress, form.stress),
          focus: numberToField(payload.focus, form.focus),
        },
      };
    case "symptoms": {
      const symptoms = Array.isArray(payload.symptoms)
        ? payload.symptoms.filter((item): item is string => typeof item === "string")
        : [];
      return {
        actionId,
        form: {
          ...form,
          symptomLabel: symptoms[0] ?? payloadString(payload.label) ?? form.symptomLabel,
          symptomSeverity: numberToField(payload.severity, form.symptomSeverity),
        },
      };
    }
    case "caffeine":
      return {
        actionId,
        form: {
          ...form,
          intakeType: intakeTypeFromPayload(payloadString(payload.intakeType), form.intakeType),
          intakeLabel: payloadString(payload.amountLabel ?? payload.name) ?? form.intakeLabel,
          caffeineCups:
            cupsFromAmountLabel(payloadString(payload.amountLabel ?? payload.name)) ??
            form.caffeineCups,
          caffeineMg: numberToField(payload.caffeineMg, form.caffeineMg),
          alcoholServings: numberToField(payload.alcoholServings, form.alcoholServings),
        },
      };
    case "workout":
      return {
        actionId,
        form: {
          ...form,
          workoutActivity: classifyWorkout(payload).label,
          workoutDurationMinutes: numberToField(
            workoutMinutes(payload),
            form.workoutDurationMinutes,
          ),
          workoutIntensity: workoutIntensityFromPayload(
            payloadString(payload.intensity),
            form.workoutIntensity,
          ),
        },
      };
    case "medication":
      return {
        actionId,
        form: {
          ...form,
          medication: payloadString(payload.medication) ?? form.medication,
          medicationStatus: medicationStatusFromPayload(
            payloadString(payload.status),
            form.medicationStatus,
          ),
          doseAmount: numberToField(payload.doseAmount, form.doseAmount),
          injectionSite: payloadString(payload.injectionSite) ?? form.injectionSite,
          cadenceDays: numberToField(payload.cadenceDays, form.cadenceDays),
        },
      };
    case "vitals":
      return {
        actionId,
        form: {
          ...form,
          vitalsMetric: vitalsMetricFromPayload(
            payloadString(payload.manualMetric),
            form.vitalsMetric,
          ),
          kg: numberToField(payload.kg ?? payload.value, form.kg),
          systolic: numberToField(payload.systolic, form.systolic),
          diastolic: numberToField(payload.diastolic, form.diastolic),
          glucoseMgDl: numberToField(payload.glucoseMgDl ?? payload.value, form.glucoseMgDl),
          bpm: numberToField(payload.bpm ?? payload.value, form.bpm),
          hrvMs: numberToField(payload.hrvMs ?? payload.rmssd_ms ?? payload.value, form.hrvMs),
          temperatureC: numberToField(payload.temperatureC ?? payload.value, form.temperatureC),
          hba1cPercent: numberToField(payload.hba1cPercent ?? payload.value, form.hba1cPercent),
        },
      };
    case "disruptor": {
      const rawReason = payloadString(payload.reason);
      const reason = CONFOUNDER_REASON_OPTIONS.find((o) => o.value === rawReason)?.value ?? "other";
      const rawDuration = payloadString(payload.duration);
      const duration =
        CONFOUNDER_DURATION_OPTIONS.find((o) => o.value === rawDuration)?.value ??
        // Older entries predate the stored preset; recover it from the window.
        confounderDurationFromWindow(occurredAt, payloadString(payload.effectiveThrough));
      return {
        actionId,
        form: {
          ...form,
          confounderReason: reason,
          confounderDuration: duration,
        },
      };
    }
    case "connection":
      return {
        actionId,
        form: {
          ...form,
          connectionType: connectionTypeFromPayload(
            payloadString(payload.connectionType),
            form.connectionType,
          ),
          connectionWho: connectionWhoFromPayload(payloadString(payload.who), form.connectionWho),
          connectionRating: numberToField(payload.rating, form.connectionRating),
        },
      };
  }
}

export function manualLogActionIdForEntryType(type: string): ManualLogActionId | null {
  switch (type) {
    case "food":
    case "hydration":
    case "sleep":
    case "mood":
    case "symptoms":
    case "caffeine":
    case "workout":
    case "medication":
    case "vitals":
    case "disruptor":
    case "connection":
      return type;
    default:
      return null;
  }
}

// Recover the duration preset a confounder was logged with from its stored
// window: no effectiveThrough means ongoing; a window of a day or less is a
// single night; anything longer is the multi-night preset. Exact enough to
// re-open the edit form on the right choice.
function confounderDurationFromWindow(
  occurredAt: string,
  effectiveThrough: string | null,
): ConfounderDuration {
  if (!effectiveThrough) return "ongoing";
  const start = Date.parse(occurredAt);
  const end = Date.parse(effectiveThrough);
  if (Number.isNaN(start) || Number.isNaN(end)) return "few_nights";
  return (end - start) / 3_600_000 <= 24 ? "one_night" : "few_nights";
}

function isoToLocalDateTimeValue(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.valueOf())) return null;
  return localDateTimeValue(date);
}

function payloadString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function payloadNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Coerce a payload number to the form's string field, keeping the default when absent. */
function numberToField(value: unknown, fallback: string): string {
  const parsed = payloadNumber(value);
  return parsed === null ? fallback : String(parsed);
}

function cleanManualNote(note: string): string {
  return note
    .split(/\r?\n/)
    .filter((line) => !line.trim().startsWith("Nutrition quality:"))
    .join("\n")
    .trim();
}

function sleepQualityLabel(quality: ManualLogForm["sleepQuality"]): string {
  // Mirrors mobile's qualityLabel: "ok" reads as "OK sleep", not "Ok sleep".
  if (quality === "ok") return "OK";
  return capitalize(quality);
}

function sleepQualityFromPayload(
  value: string | null,
  fallback: ManualLogForm["sleepQuality"],
): ManualLogForm["sleepQuality"] {
  if (
    value === "poor" ||
    value === "ok" ||
    value === "good" ||
    value === "great" ||
    value === "restless"
  ) {
    return value;
  }
  return fallback;
}

function intakeTypeFromPayload(
  value: string | null,
  fallback: api.ManualIntakeType,
): api.ManualIntakeType {
  if (
    value === "coffee" ||
    value === "tea" ||
    value === "energy_drink" ||
    value === "caffeine" ||
    value === "alcohol" ||
    value === "none" ||
    value === "other"
  ) {
    return value;
  }
  return fallback;
}

function workoutIntensityFromPayload(
  value: string | null,
  fallback: ManualLogForm["workoutIntensity"],
): ManualLogForm["workoutIntensity"] {
  if (value === "easy" || value === "moderate" || value === "hard") return value;
  return fallback;
}

function medicationStatusFromPayload(
  value: string | null,
  fallback: api.ManualMedicationStatus,
): api.ManualMedicationStatus {
  if (
    value === "taken" ||
    value === "skipped" ||
    value === "delayed" ||
    value === "moved" ||
    value === "note"
  ) {
    return value;
  }
  return fallback;
}

function connectionTypeFromPayload(
  value: string | null,
  fallback: ManualLogForm["connectionType"],
): ManualLogForm["connectionType"] {
  const parsed = connectionTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : fallback;
}

function connectionWhoFromPayload(
  value: string | null,
  fallback: ManualLogForm["connectionWho"],
): ManualLogForm["connectionWho"] {
  const parsed = connectionWhoSchema.safeParse(value);
  return parsed.success ? parsed.data : fallback;
}

function vitalsMetricFromPayload(
  value: string | null,
  fallback: api.ManualVitalsMetric,
): api.ManualVitalsMetric {
  if (
    value === "weight" ||
    value === "blood_pressure" ||
    value === "glucose" ||
    value === "resting_hr" ||
    value === "hrv" ||
    value === "temperature" ||
    value === "hba1c"
  ) {
    return value;
  }
  return fallback;
}

function cupsFromAmountLabel(value: string | null): string | null {
  if (!value) return null;
  const match = value.match(/^(\d+(?:\.\d+)?)/);
  return match?.[1] ?? null;
}

export function vitalMetricLabel(metric: api.ManualVitalsMetric): string {
  const labels: Record<api.ManualVitalsMetric, string> = {
    weight: "Weight",
    blood_pressure: "Blood pressure",
    glucose: "Glucose",
    resting_hr: "Resting heart rate",
    hrv: "HRV",
    temperature: "Temperature",
    hba1c: "HbA1c",
  };
  return labels[metric];
}

/**
 * A capture-prompt tap opens the log form for `actionId`, with `form` applied
 * onto the defaults so the tap lands on the exact field it names.
 */
export type ManualLogPreselect = {
  actionId: ManualLogActionId;
  form?: Partial<ManualLogForm>;
};

/**
 * Translate a capture prompt's log target (nextAction.capture) into a modal
 * preselect: the logAction becomes the manual-log action id and its preselect
 * fields map onto the matching form fields. Returns null for an unknown log
 * action so the modal falls back to the picker rather than a dead form.
 */
export function preselectFromCapture(capture: {
  logAction: string;
  preselect?: { vitalsMetric?: string; medicationStatus?: string; mealType?: string };
}): ManualLogPreselect | null {
  const actionId = manualLogActionIdForEntryType(capture.logAction);
  if (!actionId) return null;
  const form: Partial<ManualLogForm> = {};
  const p = capture.preselect;
  if (p?.vitalsMetric) form.vitalsMetric = p.vitalsMetric as ManualLogForm["vitalsMetric"];
  if (p?.medicationStatus) {
    form.medicationStatus = p.medicationStatus as ManualLogForm["medicationStatus"];
  }
  if (p?.mealType) form.foodMealType = p.mealType;
  return { actionId, form };
}

/**
 * Reason chips for the confounder form, in display order. Labels come from the
 * shared source so the form and the timeline copy cannot drift.
 */
export const CONFOUNDER_REASON_OPTIONS: readonly { value: ConfounderReason; label: string }[] = (
  ["heat", "illness", "caregiving", "travel", "noise", "other"] as const
).map((value) => ({ value, label: CONFOUNDER_REASON_LABELS[value] }));

/** Duration presets for the confounder form, in display order. Labels are shared. */
export const CONFOUNDER_DURATION_OPTIONS: readonly { value: ConfounderDuration; label: string }[] =
  (["one_night", "few_nights", "ongoing"] as const).map((value) => ({
    value,
    label: CONFOUNDER_DURATION_LABELS[value],
  }));

/** How-did-you-connect chips for the connection form, in display order. Labels are shared. */
export const CONNECTION_TYPE_OPTIONS: readonly { value: ConnectionType; label: string }[] = (
  ["in_person", "call", "video", "message"] as const
).map((value) => ({ value, label: CONNECTION_TYPE_LABELS[value] }));

/** Who-with chips for the connection form, in display order. Labels are shared. */
export const CONNECTION_WHO_OPTIONS: readonly { value: ConnectionWho; label: string }[] = (
  ["family", "friend", "coworker", "community"] as const
).map((value) => ({ value, label: CONNECTION_WHO_LABELS[value] }));
